import { useState, useEffect } from 'react';
import api from '@/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { KeyRound, Mail, ExternalLink, Loader2, Copy, Eye, EyeOff, CheckCircle2, AlertCircle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function CustomerLoginCard({ customerId, currentUser }) {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPwModal, setShowPwModal] = useState(false);
  const [mode, setMode] = useState('admin_set');  // 'admin_set' or 'auto_email'
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [forceLogout, setForceLogout] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resultPw, setResultPw] = useState('');

  const isAdmin = currentUser?.role === 'master_admin' || currentUser?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/users/customer-login/${customerId}`);
      setInfo(r.data);
    } catch {
      setInfo(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (customerId) load(); }, [customerId]);

  const openModal = () => {
    setMode('admin_set');
    setPw1(''); setPw2(''); setResultPw('');
    setForceLogout(true);
    setShowPwModal(true);
  };

  const submit = async () => {
    if (mode === 'admin_set') {
      if (pw1.length < 8) { toast.error('Password must be at least 8 characters'); return; }
      if (!(/[a-zA-Z]/.test(pw1) && /\d/.test(pw1))) { toast.error('Password must contain letters and numbers'); return; }
      if (pw1 !== pw2) { toast.error('Passwords do not match'); return; }
    }
    setSaving(true);
    try {
      const r = await api.post('/users/customer-set-password', {
        customer_id: customerId,
        mode,
        password: mode === 'admin_set' ? pw1 : undefined,
        force_logout: forceLogout,
      });
      if (mode === 'auto_email') {
        if (r.data.emailed) {
          toast.success(`Temp password emailed to ${r.data.user_email}`);
          setShowPwModal(false);
        } else {
          toast.warning('Email failed — password shown below for manual sharing');
          setResultPw(r.data.password);
        }
      } else {
        setResultPw(r.data.password);
        toast.success('Password set. Share it with the customer.');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const copyPw = () => { navigator.clipboard.writeText(resultPw); toast.success('Password copied'); };

  if (loading) return null;
  if (!info?.email) return null; // no contact email, hide card

  return (
    <div className="card-slate p-4" data-testid="customer-login-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <KeyRound size={15} style={{ color: 'var(--text-muted)' }} />
          <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Customer Login</p>
          {info.has_login ? (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm flex items-center gap-1" style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}>
              <CheckCircle2 size={9} /> Active
            </span>
          ) : (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>
              No Account
            </span>
          )}
        </div>
        {isAdmin && info.has_login && (
          <button onClick={() => navigate(`/admin/users?search=${info.email}`)}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded-sm border transition-colors font-['IBM_Plex_Sans']"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
            data-testid="open-user-account-btn">
            <ExternalLink size={11} /> Manage in Users
          </button>
        )}
      </div>

      <div className="space-y-1 mb-3">
        <p className="text-xs font-mono" style={{ color: 'var(--text-primary)' }} data-testid="customer-login-email">{info.email}</p>
        {info.has_login && (
          <p className="text-[10px]" style={{ color: 'var(--text-faint)' }}>
            {info.last_login_at ? `Last login: ${new Date(info.last_login_at).toLocaleString()}` : 'Last login: never'}
            {info.created_at && <> · Created: {new Date(info.created_at).toLocaleDateString()}</>}
          </p>
        )}
      </div>

      {isAdmin && (
        <button onClick={openModal} data-testid="set-customer-password-btn"
          className="w-full py-2 px-3 rounded-md text-xs font-bold uppercase tracking-wide border-2 transition-colors flex items-center justify-center gap-2"
          style={{ borderColor: 'var(--primary)', color: 'var(--primary)', background: 'transparent' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#1A120E'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--primary)'; }}>
          <KeyRound size={13} /> {info.has_login ? 'Reset / Set Password' : 'Create Login & Set Password'}
        </button>
      )}

      <Dialog open={showPwModal} onOpenChange={(o) => { setShowPwModal(o); if (!o) { setResultPw(''); setPw1(''); setPw2(''); } }}>
        <DialogContent className="max-w-lg" data-testid="set-password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound size={18} /> Set Customer Password</DialogTitle>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{info.email}</p>
          </DialogHeader>

          {!resultPw ? (
            <div className="space-y-4 py-2">
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Method</p>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${mode === 'admin_set' ? 'border-[var(--primary)]' : 'border-[var(--border)]'}`}>
                    <input type="radio" checked={mode === 'admin_set'} onChange={() => setMode('admin_set')} className="mt-0.5" data-testid="mode-admin-set" />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>I'll set the password</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Type a password yourself and share it with the customer (phone, SMS, in person).</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${mode === 'auto_email' ? 'border-[var(--primary)]' : 'border-[var(--border)]'}`}>
                    <input type="radio" checked={mode === 'auto_email'} onChange={() => setMode('auto_email')} className="mt-0.5" data-testid="mode-auto-email" />
                    <div>
                      <p className="text-sm font-semibold flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}><Mail size={12} /> Generate & email random password</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>System creates a strong password and emails it directly to the customer.</p>
                    </div>
                  </label>
                </div>
              </div>

              {mode === 'admin_set' && (
                <>
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>New Password * (min 8 chars, letters + numbers)</label>
                    <div className="relative">
                      <input type={showPw ? 'text' : 'password'} value={pw1} onChange={e => setPw1(e.target.value)} data-testid="new-password-input"
                        className="w-full h-10 px-3 pr-10 rounded-md text-sm focus:outline-none"
                        style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
                      <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1">
                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Confirm Password *</label>
                    <input type={showPw ? 'text' : 'password'} value={pw2} onChange={e => setPw2(e.target.value)} data-testid="confirm-password-input"
                      className="w-full h-10 px-3 rounded-md text-sm focus:outline-none"
                      style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
                  </div>
                </>
              )}

              <label className="flex items-center gap-2 cursor-pointer" data-testid="force-logout-toggle">
                <input type="checkbox" checked={forceLogout} onChange={e => setForceLogout(e.target.checked)} className="w-4 h-4 accent-yellow-500" />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Force logout existing sessions (recommended)</span>
              </label>

              <div className="p-3 rounded-md flex items-start gap-2 text-[11px]" style={{ background: 'rgba(246,194,50,0.08)', border: '1px solid rgba(246,194,50,0.25)', color: 'var(--text-muted)' }}>
                <ShieldAlert size={13} style={{ color: 'var(--primary)' }} className="mt-0.5 flex-shrink-0" />
                <span>This action is audit logged. Tell the customer to change their password after first login.</span>
              </div>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="p-3 rounded-md flex items-start gap-2" style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <CheckCircle2 size={14} style={{ color: '#22C55E' }} className="mt-0.5" />
                <p className="text-xs" style={{ color: 'var(--text-primary)' }}>Password set. Copy and share it securely with the customer.</p>
              </div>
              <div>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Temporary Password</p>
                <div className="flex items-center gap-2 p-3 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <span className="flex-1 font-mono text-sm" style={{ color: 'var(--primary)' }}>{resultPw}</span>
                  <button onClick={copyPw} className="p-1.5 rounded hover:bg-white/5" data-testid="copy-password-btn">
                    <Copy size={14} style={{ color: 'var(--text-muted)' }} />
                  </button>
                </div>
              </div>
              <p className="text-[10px]" style={{ color: 'var(--text-faint)' }}>This is the only time you'll see this password. It cannot be retrieved later.</p>
            </div>
          )}

          <DialogFooter>
            {!resultPw ? (
              <>
                <Button variant="outline" onClick={() => setShowPwModal(false)} disabled={saving}>Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="submit-password-btn">
                  {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving...</> : (mode === 'auto_email' ? 'Generate & Email' : 'Set Password')}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowPwModal(false)}>Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
